import axios from 'axios';
import * as CONSTANTS from './constants';

/**
 * ActionLogger handles logging actions taken by the players
 * The purpose is to create a file that can be given to the teacher after the game to see all the actions that students took and how they did
 * The Logger is built as a singleton so that it can be easily accessed regardless of where it is needed
 */
export class ActionLogger {
  static instance = null;
  filename;
  endGameReport;
  file;

  /**
   * Factory function
   */
  static build(sessionId) {
    this.instance = new ActionLogger();
    this.instance.filename = 'cirtgame_' + this.instance.getTimestamp() + sessionId + '.log';
    this.instance.file = null // FIXME: convert this to javascript: fopen(self::$instance->filename, "a");
    this.instance.logSessionInfo();
    this.instance.endGameReport = [];
    return this.instance;
  }

  /**
   * singleton getter function
   * This function can be used anywhere in CIRT to get the logger
   * If no logger exists and a sesion_id is passed in, a logger will be constructed
   * @param {number} sessionId = null
   * @return {ActionLogger} instance
   */
  static getLogger(sessionId = null) {
    if (this.instance === null) {
      if (sessionId === null) {
        throw new Error('sessionId is required');
      }
      ActionLogger.build(sessionId);
    }
    return this.instance;
  }

  /**
   * sets the static action logger
   * @param {ActionLogger} logger
   */
  static setLogger(logger) {
    this.instance = logger;
  }

  constructor() {}

  /**
   * Log the beginning of the round and associated data
   * @param {Session} currSession
   * @param {Storage} storage
   * @param {Storage} storageWorstCase
   * @param {RedHerringManager} redHerringManager
   */
  logRoundBegins(currSession, storage, storageWorstCase, redHerringManager) {
    let newRound = currSession.getRound();
    let roundCountMs = "\n" + this.getTimestamp() + "\tROUND BEGINS - At the beginning of Round " + newRound + ", the game stands:";
    this.logToFile(roundCountMs);
    this.logRoundCost(currSession.calculateLossIncurred(), currSession.getFinalReport());
    let shutoffNodes = storage.getNodesByStatus(CONSTANTS.OFF)
    this.logRoundStatusOff(shutoffNodes.length, shutoffNodes);
    this.infectedNodes = storage.getNodesByStatus(CONSTANTS.COMPROMISED);
    let infectedNodesWorstCase = storageWorstCase.getNodesByStatus(CONSTANTS.COMPROMISED);
    this.logRoundInfectedNodes(storage, this.infectedNodes.length, storage.getNodesByStatus(CONSTANTS.COMPROMISED), infectedNodesWorstCase.length, infectedNodesWorstCase);
    CONSTANTS.ALL_ROLES.forEach(role => {
      this.logRoundRedHerrings(role, redHerringManager.getRoleRedHerrings(role));
    });
  }

  /** Writes total cost visible to players to log file
   * @param {number} totalRoundCosts
   * @param {number[]} costs
   */
  logRoundCost(totalRoundCosts, costs) {
    let timestamp = this.getTimestamp();
    let costInMs = "\n" + timestamp + "\t\tCOSTS - Total Incurred Loss: " + totalRoundCosts;
    this.logToFile(costInMs);
    if (costs && costs.length) {
      this.endGameReport.totalIncurredLoss = totalRoundCosts;
      this.endGameReport.totalLossFromInfection = costs.totalLossFromInfection;
      this.endGameReport.worstCase = costs.worstCase;
      costInMs = "\n" + timestamp + "\t\tCOSTS - Total Loss From Infection: " + costs.totalLossFromInfection;
      this.logToFile(costInMs);
      this.endGameReport.totalLossFromShutoff = costs.shutoffNodes;
      costInMs = "\n" + timestamp + "\t\tCOSTS - Total Loss From Shutoff: " + costs.shutoffNodes;
      this.logToFile(costInMs);
      this.endGameReport.savingsFromNotiifications = costs.savingsFromNotifications;
      costInMs = "\n" + timestamp + "\t\tCOSTS - Savings From Notifications: " + costs.savingsFromNotifications;
      this.logToFile(costInMs);
    }
  }

  /** Writes total number of shut off nodes and list of those node names to log file
   * @param {number} numOff
   * @param {string} listOfNodes
   */
  logRoundStatusOff(numOff, listOfNodes) {
    let onNodeNames = []
    let offCountMs = "\n" + this.getTimestamp() + "\t\tNumber of Shut off nodes: " + numOff;
    this.logToFile(offCountMs);
    let offListMs = "\n" + this.getTimestamp() + "\t\tShut off node IDs: [";
    listOfNodes.forEach(node => {
      offListMs += node + ", ";
      onNodeNames.push(node);
    })
    if (onNodeNames.length) {
      offListMs = offListMs.substring(0, offListMs.length - 2);
    }
    offListMs += "]";
    this.logToFile(offListMs);
    this.endGameReport.shutOffNodeCount = numOff;
    this.endGameReport.shutOffNodes = onNodeNames;
  }

/****************************************************************************
 *                        BASIC LOG MESSAGES                                *
 ****************************************************************************/

  /**
   * Log with "INFO" prepended
   * @param {string} message
   */
  log(message) {
    this.logWithCategory(message, 'INFO');
  }

  /**
   * Log with "WARN" category
   * @param {string} message
   */
  warn(message) {
    this.logWithCategory(message, 'WARN');
  }

  /**
   * Log with "ERROR" prepended
   * @param {string} message
   */
  error(message) {
    this.logWithCategory(message, 'ERROR');
  }

  /**
   * Log with "ACTION" prepended
   * @param {string} message
   */
  action(message) {
    this.logWithCategory(message, 'ACTION');
  }

  /**
   * Log with "INFO" prepended
   * @param {string} message
   */
  info(message) {
    this.logWithCategory(message, 'INFO');
  }

  /**
   * Log with "DEBUG" category. THESE SHOULD BE REMOVED IN PRODUCTION
   * @param {string} message
   */
  debug(message) {
    this.logWithCategory(message, 'DEBUG');
  }

  /**
   * Take a message and prepend the timestamp and category and print it to the log file
   * @param {string} message
   * @param {string} category
   */
  logWithCategory(message, category) {
    this.logToFile("\n" + this.getTimestamp() + "\t" + category + ": " + message);
  }

  /**
   * print message to log file
   * There is a chance that file_put_contents will fail. If this happens it will echo the problem
   * @param {string} message
   */
  logToFile(message) {
    try {
      axios.post('/api/cirt/logToFile', {
        params: {
          message,
          filename: this.filename
        }
      })
    } catch (exception) {
      console.log(exception);
      // Apparently they wanted to do something to get the log working in this case but idk
    }
  }

  /**
   * Log Session Info
   * Currently only called in ActionLogger::build()
   */
  logSessionInfo() {
    let message = this.getTimestamp() + "\tBEGIN SESSION: ";
    try {
      axios.post('/api/cirt/logToFile', {
        params: {
          message,
          filename: this.filename
        }
      })
    } catch (exception) {
      console.log(exception);
    }
  }

  /**
   * get current time stamp as string
   * @return {string} timestamp
   */
  getTimestamp() {
    return new Date().toISOString();
  }

  /**
   * Output infection status to log file
   * @param {Storage} storage
   * @param {number} numInfected
   * @param {string} listINodes | list of infected nodes
   * @param {number} worstCaseNumInfected
   * @param {string} worstCaseNodes
   */
  logRoundInfectedNodes(storage, numInfected, listINodes, worstCaseNumInfected, worstCaseNodes) {
    let INodeNames = []
    let iNodesNamesWorstCase = []
    let infectListMsWc = '';
    let infectListMs = '';
    let infectedCountMs = "\n" + this.getTimestamp() + "\t\tNumber of Infected nodes: " + numInfected;
    this.logToFile(infectedCountMs);
    infectListMs = "\n" + this.getTimestamp() + "\t\tInfected node IDs: [";
    let infectListMsWorstCase = "\n" + this.getTimestamp() + "\t\tWorst case infected node IDs: [";
    listINodes.forEach(nodeName => {
      let node = storage.getNodeData(nodeName);
      infectListMs += node.getName() + " (visible to: " + node + JSON.stringify(node.getAttackVisibility()) + "), ";
      INodeNames.push(node.getName());
    });
    worstCaseNodes.forEach(node => {
      infectListMsWc += node + " (visible to: " + node + "), ";
      iNodesNamesWorstCase.push(node);
    });
    if (worstCaseNodes.length) {
      // The stuff in the PHP version never worked so I'm not going to bother with it
    }
    infectListMs += "]";
    infectListMsWorstCase += "]";
    this.logToFile(infectListMs);
    this.endGameReport.infectedNodeCount = numInfected;
    this.endGameReport.infectedNodes = INodeNames;
    this.endGameReport.worstCaseInfectedNodeCount = worstCaseNumInfected;
    this.endGameReport.worstCaseInfectedNodes = iNodesNamesWorstCase;
  }

  /**
   * output where Red Herrings are to log file
   * @param {string} role
   * @param redHerrings
   */
  logRoundRedHerrings(role, redHerrings) {
    let redHerringsListMs = "\n" + this.getTimestamp() + "\t\tRed Herrings for " + role + ": " + JSON.stringify(redHerrings);
    this.logToFile(redHerringsListMs);
    this.endGameReport["redHerringsFor-" + role] = redHerrings;
  }

  toJSON() {
    return {
      filename: this.filename,
      end_game_report: this.endGameReport
    }
  }

  static jsonDeserialize(jsonString) {
    this.instance = new ActionLogger();
    let data = jsonString;
    this.instance.filename = data.filename;
    this.instance.endGameReport = data.end_game_report;
  }

  /**
   * Log the end of the game
   * @param {Session} currSession
   */
  logEndGame(currSession) {
    let endTime = this.getTimestamp();
    let endGameMs = "\n" + endTime + "\tEND GAME -::- All rounds have been completed. The final tallies stand:";
    this.logToFile(endGameMs);
    this.endGameReport.gameEndTimestamp = endTime;
    this.logRoundCost(currSession.calculateLossIncurred(), currSession.getFinalReport());
    let shutoffNodes = currSession.getStorage().getNodesByStatus(CONSTANTS.OFF);
    this.logRoundStatusOff(shutoffNodes.length, shutoffNodes);
    let infectedNodes = currSession.getStorage().getNodesByStatus(CONSTANTS.COMPROMISED);
    let infectedNodesWorstCase = currSession.getStorageWorstCase().getNodesByStatus(CONSTANTS.COMPROMISED);
    this.logRoundInfectedNodes(currSession.getStorage(), infectedNodes.length, infectedNodes, infectedNodesWorstCase.length, infectedNodesWorstCase);
    CONSTANTS.ALL_ROLES.forEach(role => {
      this.logRoundRedHerrings(role, currSession.getRedHerringManager().getRoleRedHerrings(role));
    });
    currSession.finalResults = this.endGameReport;
  }
}